/**
 * GuidedFlow — design tokens, category mappings and pure helpers.
 *
 * Extracted from GuidedFlow.js to make the main state machine easier to read.
 * IMPORTANT: `T` and `S` are mutable singletons that get re-populated when
 * the user toggles "Modo Sol" via `applyTheme`. Both objects keep the same
 * reference so any consumer that already imported them gets the new values
 * on the next render (no context refactor needed).
 */
import { Wine, GlassWater, Beer, Martini, Coffee, CupSoda, Leaf } from 'lucide-react';

/* ─────────── Design tokens ─────────── */
export const T_DARK = {
  bg: '#161514', bgEl: '#22201F', border: '#33302E',
  gold: '#C5A059', goldHover: '#D4AF37', goldDim: '#8A7342',
  text: '#F9F6F0', textSec: '#A39F98',
  font: { h: "'Playfair Display', serif", b: "'DM Sans', sans-serif" },
};
export const T_LIGHT = {
  bg: '#FBF6E9', bgEl: '#FFFDF6', border: '#C9B98A',
  gold: '#6B4F1A', goldHover: '#4F3A0F', goldDim: '#8E6F33',
  text: '#1A1612', textSec: '#5C5246',
  font: { h: "'Playfair Display', serif", b: "'DM Sans', sans-serif" },
};
export const T = { ...T_DARK };
export const applyTheme = (mode) => {
  Object.assign(T, mode === 'sun' ? T_LIGHT : T_DARK);
  Object.assign(S, buildS());
};

/* ─────────── Category mappings ─────────── */
export const ALCOHOL_CATS = [
  { id: 'cat-bodega', icon: Wine },
  { id: 'cat-cervezas', icon: Beer },
  { id: 'cat-cocteles', icon: Martini },
  { id: 'cat-licores', icon: GlassWater },
];
export const NO_ALCOHOL_CATS = [
  { id: 'cat-refrescos', icon: CupSoda },
  { id: 'cat-cafe-plantas', icon: Coffee },
  { id: 'cat-chocolates-1ca5ec62', icon: Coffee },
  { id: 'cat-matcha-23fd648d', icon: Leaf },
  { id: 'cat-infusiones-161d80f8', icon: Leaf },
  { id: '7be9dd0e-2121-44c3-a063-ebb6e1e8e0dc', icon: Leaf }, // Tés
];
export const FOOD_CATS = [
  { id: 'cat-picar' },
  { id: 'cat-ensaladas' },
  { id: 'cat-bocadillos-tradicionales' },
  { id: 'cat-bocadillos-especiales' },
  { id: 'cat-pizzas' },
  { id: 'cat-dulces' },
  { id: 'cat-reposteria' },
];

/* ─────────── Category label translations by id ─────────── */
export const CAT_LABELS = {
  'cat-bodega': { cat: 'Vins i Vermuts', es: 'Vinos y Vermuts', en: 'Wines & Vermouths', fr: 'Vins et Vermouths' },
  'cat-cervezas': { cat: 'Cerveses', es: 'Cervezas', en: 'Beers', fr: 'Bi\u00e8res' },
  'cat-cocteles': { cat: 'C\u00f2ctels', es: 'C\u00f3cteles', en: 'Cocktails', fr: 'Cocktails' },
  'cat-licores': { cat: 'Licors', es: 'Licores', en: 'Spirits', fr: 'Spiritueux' },
  'cat-refrescos': { cat: 'Refrescos i Sucs', es: 'Refrescos y Zumos', en: 'Soft Drinks & Juices', fr: 'Sodas et Jus' },
  'cat-cafe-plantas': { cat: 'Cafes', es: 'Caf\u00e9s', en: 'Coffees', fr: 'Caf\u00e9s' },
  'cat-chocolates-1ca5ec62': { cat: 'Xocolates', es: 'Chocolates', en: 'Chocolates', fr: 'Chocolats' },
  'cat-matcha-23fd648d': { cat: 'Matcha', es: 'Matcha', en: 'Matcha', fr: 'Matcha' },
  'cat-infusiones-161d80f8': { cat: 'Infusions', es: 'Infusiones', en: 'Herbal teas', fr: 'Infusions' },
  '7be9dd0e-2121-44c3-a063-ebb6e1e8e0dc': { cat: 'Tes', es: 'T\u00e9s', en: 'Teas', fr: 'Th\u00e9s' },
  'cat-picar': { cat: 'Per picar', es: 'Para Picar', en: 'To Share', fr: '\u00c0 partager' },
  'cat-ensaladas': { cat: 'Amanides', es: 'Ensaladas', en: 'Salads', fr: 'Salades' },
  'cat-bocadillos-tradicionales': { cat: 'Entrep\u00e0 cl\u00e0ssics', es: 'Bocadillos Cl\u00e1sicos', en: 'Classic Sandwiches', fr: 'Sandwichs classiques' },
  'cat-bocadillos-especiales': { cat: 'Entrep\u00e0 especials', es: 'Bocadillos Especiales', en: 'Special Sandwiches', fr: 'Sandwichs sp\u00e9ciaux' },
  'cat-pizzas': { cat: 'Pizzes Medievals', es: 'Pizzas Medievales', en: 'Medieval Pizzas', fr: 'Pizzas m\u00e9di\u00e9vales' },
  'cat-dulces': { cat: 'Dol\u00e7os', es: 'Dulces', en: 'Sweets', fr: 'Sucreries' },
  'cat-reposteria': { cat: 'Rebosteria', es: 'Reposter\u00eda', en: 'Pastries', fr: 'P\u00e2tisseries' },
};

export const catLabel = (id, lang) => CAT_LABELS[id]?.[lang] || CAT_LABELS[id]?.es || id;

export function isCafeOpen(cutoff = 12.5) {
  const now = new Date();
  return (now.getHours() + now.getMinutes() / 60) < cutoff;
}

/* ─────────── Product sorting strategies ─────────── */
export function sortProducts(products, strategy) {
  if (!strategy || strategy === 'default') return products;
  const sorted = [...products];
  switch (strategy) {
    case 'premium_first':
      return sorted.sort((a, b) => {
        const aP = (a.badge_premium || a.is_premium ? 4 : 0) + (a.badge_bestseller || a.is_featured ? 2 : 0) + (a.badge_nuevo ? 1 : 0);
        const bP = (b.badge_premium || b.is_premium ? 4 : 0) + (b.badge_bestseller || b.is_featured ? 2 : 0) + (b.badge_nuevo ? 1 : 0);
        if (bP !== aP) return bP - aP;
        return (b.price || 0) - (a.price || 0);
      });
    case 'price_high':
      return sorted.sort((a, b) => (b.price || 0) - (a.price || 0));
    case 'price_low':
      return sorted.sort((a, b) => (a.price || 0) - (b.price || 0));
    case 'bestseller_first':
      return sorted.sort((a, b) => {
        const aScore = (a.badge_bestseller || a.is_featured ? 2 : 0) + (a.is_premium || a.badge_premium ? 1 : 0);
        const bScore = (b.badge_bestseller || b.is_featured ? 2 : 0) + (b.is_premium || b.badge_premium ? 1 : 0);
        return bScore - aScore;
      });
    default:
      return products;
  }
}

/* ─────────── Auto-recommendation: badge-driven ─────────── */
export function getAutoRecommendations(products, maxCount = 2) {
  const premium = products.filter(p => p.badge_premium || p.is_premium);
  const bestseller = products.filter(p => (p.badge_bestseller || p.is_featured) && !p.badge_premium && !p.is_premium);
  const byPrice = [...products].sort((a, b) => (b.price || 0) - (a.price || 0));

  const result = [];
  for (const p of premium) { if (result.length < maxCount && !result.find(r => r.id === p.id)) result.push(p); }
  for (const p of bestseller) { if (result.length < maxCount && !result.find(r => r.id === p.id)) result.push(p); }
  for (const p of byPrice) { if (result.length < maxCount && !result.find(r => r.id === p.id)) result.push(p); }

  return result;
}

/* ─────────── Shared styles ─────────── */
export const buildS = () => ({
  screen: {
    position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column',
    overflow: 'auto', WebkitOverflowScrolling: 'touch',
    background: T.bg,
    alignItems: 'stretch',
  },
  screenInner: {
    width: '100%', maxWidth: '640px', margin: '0 auto',
    paddingTop: 'calc(env(safe-area-inset-top, 0px) + 76px)',
    paddingLeft: '24px', paddingRight: '24px', paddingBottom: '32px',
    flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'safe center',
    minHeight: '100%',
  },
  actionsCol: {
    width: '100%', maxWidth: '460px', margin: '0 auto',
  },
  question: {
    fontFamily: T.font.h, fontSize: 'clamp(28px, 4.8vw, 51px)', fontWeight: 500, color: T.text,
    lineHeight: 1.15, marginBottom: '8px', letterSpacing: '-0.01em',
  },
  sub: {
    fontFamily: T.font.b, fontSize: 'clamp(16px, 2vw, 20px)', color: T.textSec, lineHeight: 1.5, marginBottom: '32px',
  },
  card: {
    display: 'flex', alignItems: 'center', gap: '16px', padding: '20px',
    background: T.bgEl, border: `1px solid ${T.border}`, borderRadius: '16px',
    cursor: 'pointer', transition: 'all 0.2s', width: '100%', textAlign: 'left',
  },
  goldBtn: {
    width: '100%', padding: '18px', borderRadius: '999px', border: 'none',
    background: T.gold, color: T.bg, fontFamily: T.font.b, fontSize: 'clamp(16px, 1.8vw, 19px)',
    fontWeight: 600, cursor: 'pointer', transition: 'all 0.2s', letterSpacing: '0.01em',
  },
  ghostBtn: {
    width: '100%', padding: '20px', borderRadius: '999px',
    border: `1px solid ${T.border}`, background: 'transparent',
    color: T.text, fontFamily: T.font.b, fontSize: 'clamp(18px, 1.8vw, 21px)', fontWeight: 500,
    cursor: 'pointer', transition: 'all 0.2s',
  },
  price: {
    fontFamily: T.font.b, fontSize: '15px', fontWeight: 600, color: T.gold,
  },
});

// Singleton style object. Mutated in place by `applyTheme` so existing imports
// always see the latest palette without forcing a re-render through context.
export const S = buildS();
